import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Ship, TrendingUp, Shield, ArrowRight, Check, Book, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, processSession } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Fetch subscription if user is authenticated
  useEffect(() => {
    const fetchSubscription = async () => {
      if (user) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/subscription`, {
            withCredentials: true
          });
          setSubscription(response.data);
        } catch (error) {
          console.error('[LandingPage] Error fetching subscription:', error);
        }
      }
    };
    
    fetchSubscription();
  }, [user]);

  useEffect(() => {
    console.log('[LandingPage] Current user:', user);
    console.log('[LandingPage] URL hash:', window.location.hash);
    console.log('[LandingPage] Full URL:', window.location.href);
    
    // Check if we should scroll to pricing (from Usage page navigation)
    if (location.state?.scrollToPricing) {
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing-section');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      // Don't redirect to dashboard if we're intentionally viewing pricing
      return;
    }
    
    // If already authenticated and NOT viewing pricing, go to product selector
    if (user) {
      console.log('[LandingPage] User already authenticated, redirecting to product selector');
      navigate('/selector');
      return;
    }

    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      setProcessing(true);
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      console.log('[LandingPage] Processing session ID:', sessionId.substring(0, 10) + '...');
      
      processSession(sessionId)
        .then(() => {
          console.log('[LandingPage] Session processed successfully');
          toast.success('Login successful! Redirecting...');
          // Clear URL fragment
          window.history.replaceState({}, document.title, window.location.pathname);
          setTimeout(() => navigate('/selector'), 500);
        })
        .catch((error) => {
          console.error('[LandingPage] Session processing error:', error);
          toast.error('Authentication failed. Please try again.');
          setProcessing(false);
          // Clear the hash to allow retry
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    } else {
      console.log('[LandingPage] No session_id in URL, showing landing page');
    }
  }, [user, navigate, processSession]);

  const handleUpgrade = async (tier) => {
    if (!user) {
      // Not logged in, start trial
      login();
    } else {
      // Already logged in, handle upgrade
      try {
        const response = await axios.patch(
          `${BACKEND_URL}/api/subscription/upgrade`,
          null,
          {
            params: { tier: tier },
            withCredentials: true
          }
        );
        
        toast.success(`Successfully upgraded to ${tier} plan!`);
        
        // Refresh subscription data
        const subResponse = await axios.get(`${BACKEND_URL}/api/subscription`, {
          withCredentials: true
        });
        setSubscription(subResponse.data);
        
        // Navigate to usage page to see updated plan
        setTimeout(() => {
          navigate('/usage');
        }, 1000);
        
      } catch (error) {
        console.error('[LandingPage] Error upgrading:', error);
        toast.error('Failed to upgrade plan. Please try again.');
      }
    }
  };

  const isCurrentPlan = (tier) => {
    return subscription && subscription.tier.toLowerCase() === tier.toLowerCase();
  };

  const getTierLevel = (tier) => {
    const levels = { starter: 1, professional: 2, enterprise: 3 };
    return levels[tier.toLowerCase()] || 0;
  };

  const isUpgrade = (tier) => {
    if (!subscription) return false;
    return getTierLevel(tier) > getTierLevel(subscription.tier);
  };

  const isDowngrade = (tier) => {
    if (!subscription) return false;
    return getTierLevel(tier) < getTierLevel(subscription.tier);
  };

  if (processing) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="processing-session">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-[hsl(var(--muted-foreground))]">Authenticating...</p>
        </div>
      </div>
    );
  }

  const features = [
    {
      icon: TrendingUp,
      title: 'Trade Analytics',
      description: 'Morning reports, opportunity radar, and real-time sales alerts'
    },
    {
      icon: Shield,
      title: 'Freight Insurance',
      description: 'Parametric quotes, risk indicators, and auto-claims processing'
    },
    {
      icon: Ship,
      title: 'Sagisu Marketplace',
      description: 'RFQ workflows, vessel-cargo matching, and deal closing'
    }
  ];

  const benefits = [
    'Unified maritime data layer',
    'Real-time vessel tracking (AIS)',
    'Port congestion analytics',
    'Bunker price monitoring',
    'Freight rate intelligence',
    'AI-powered predictions'
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,hsl(var(--background))_0%,hsl(var(--secondary))_45%,hsl(var(--accent)/0.1)_100%)]" />
        <div className="noise-overlay" />
        
        <div className="relative">
          {/* Navigation */}
          <header className="border-b bg-[hsl(var(--card))]/80 backdrop-blur">
            <div className="container max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <Ship className="h-6 w-6 text-[hsl(var(--accent))]" />
                  <span className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                    Maritime Pulse
                  </span>
                </div>
                <nav className="hidden md:flex gap-6">
                  <a 
                    href="#pricing-section" 
                    className="text-sm hover:text-[hsl(var(--accent))] transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Pricing
                  </a>
                  <a 
                    href="/docs" 
                    className="text-sm hover:text-[hsl(var(--accent))] transition-colors flex items-center gap-1"
                  >
                    <Book className="h-4 w-4" />
                    API Docs
                  </a>
                </nav>
              </div>
              <Button onClick={login} data-testid="login-button" className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90">
                Sign In with Google
              </Button>
            </div>
          </header>

          {/* Hero Content */}
          <div className="container max-w-7xl mx-auto px-4 py-20 md:py-32">
            <div className="max-w-5xl mx-auto">
              <div className="space-y-8 text-center">
                <div className="space-y-4">
                  <div className="text-sm font-semibold tracking-wide uppercase mb-2" style={{ color: '#2EE6A6', letterSpacing: '0.16em' }}>
                    Sagisu Intelligence Platform
                  </div>
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-tight" style={{ fontFamily: 'var(--font-display)' }} data-testid="hero-title">
                    Real-Time Intelligence for Commerce, Logistics & Finance
                  </h1>
                  <p className="text-xl text-[hsl(var(--muted-foreground))] leading-relaxed max-w-4xl mx-auto" data-testid="hero-subtitle">
                    Unify global commerce trends, multimodal logistics data, and trade finance signals into a single intelligence layer. Make faster decisions, reduce risk, and unlock new opportunities across the value chain.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={login}
                    data-testid="hero-cta-button"
                    className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 h-12 px-6 shadow-lg"
                  >
                    Explore Platforms
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    data-testid="hero-secondary-button"
                    className="h-12 px-6"
                    onClick={() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    View Plans
                  </Button>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Powered by unified data intelligence • Built for founders, operators, and traders
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Platforms Section */}
      <div className="py-20 md:py-32 bg-gradient-to-b from-[hsl(var(--background))] to-[hsl(var(--muted))]/10" id="platforms">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Platform Capabilities
            </h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              Specialized intelligence for commerce and maritime operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Maritime Pulse Column */}
            <div>
              <div className="mb-6 text-center md:text-left">
                <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                  <Ship className="h-6 w-6 text-[hsl(var(--accent))]" />
                  <h3 className="text-2xl font-semibold">Maritime Pulse</h3>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Logistics Intelligence
                </p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Real-time vessel tracking (AIS)</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Port congestion analytics</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Bunker price monitoring</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Freight rate intelligence</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">AI-powered predictions</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Unified maritime data layer</span>
                </li>
              </ul>
            </div>

            {/* Commerce Index Column */}
            <div>
              <div className="mb-6 text-center md:text-left">
                <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                  <TrendingUp className="h-6 w-6 text-[hsl(var(--primary))]" />
                  <h3 className="text-2xl font-semibold">Commerce Index</h3>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Commerce Intelligence
                </p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Real-time CI Score (0-1000)</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Category demand heatmaps</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Breakout SKU detection</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Creator influence tracking</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Price volatility signals</span>
                </li>
                <li className="flex items-start gap-3 p-4 rounded-lg bg-[hsl(var(--card))] border">
                  <Check className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">7/30-day demand forecasts</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Products Showcase Section */}
      <div className="py-20 md:py-32" id="features-section">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Two Specialized Intelligence Platforms
            </h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              Real-time data intelligence for commerce trends and maritime operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Commerce Index Product */}
            <Card className="relative overflow-hidden border-2 border-[hsl(var(--primary))] hover:shadow-2xl transition-all">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))]"></div>
              <CardHeader className="pb-6 pt-8">
                <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#2EE6A6', letterSpacing: '0.16em' }}>
                  Commerce Intelligence
                </div>
                <CardTitle className="text-2xl mb-3">
                  Commerce Index
                  <Badge variant="secondary" className="ml-3 text-xs">Preview</Badge>
                </CardTitle>
                <p className="text-[hsl(var(--muted-foreground))] text-sm">
                  The real-time pulse of what the world is buying. Track demand signals, breakout products, and market momentum in one benchmark.
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] italic mt-2">
                  Demo data for illustration • Full launch Q1 2025
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">CI Score (0–1000)</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Real-time benchmark combining demand, supply, price, creator, transaction, and logistics signals</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Category Heatmaps</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Track momentum across Beauty, Electronics, Fitness, Home Goods, and Pet Supplies</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Breakout SKU Detection</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Spot trending products before competitors with velocity indicators and creator influence data</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">7/30-Day Forecasts</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Data-driven projections for demand planning and inventory optimization</p>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">
                    <strong>Perfect for:</strong> DTC brands, ecommerce founders, marketplace operators, creators, and commerce analysts
                  </p>
                  <Button 
                    className="w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 shadow-lg"
                    onClick={login}
                    data-testid="cta-commerce-index"
                  >
                    Explore Commerce Index
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Maritime Pulse Product */}
            <Card className="relative overflow-hidden border-2 border-[hsl(var(--accent))] hover:shadow-2xl transition-all">
              <div className="absolute top-0 left-0 w-full h-2 bg-[hsl(var(--accent))]"></div>
              <CardHeader className="pb-6 pt-8">
                <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#2EE6A6', letterSpacing: '0.16em' }}>
                  Logistics Intelligence
                </div>
                <CardTitle className="text-2xl mb-3">Maritime Pulse</CardTitle>
                <p className="text-[hsl(var(--muted-foreground))] text-sm">
                  End-to-end maritime intelligence with vessel tracking, freight analytics, port insights, and marketplace data for global shipping operations.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                      <Ship className="h-4 w-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Live Vessel Tracking</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Track 12,709 vessels in real-time with AIS data, route analytics, and ETA predictions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-4 w-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Freight Insurance</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">AI-powered risk assessment, instant quotes, and policy management for cargo protection</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                      <Ship className="h-4 w-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Sagisu Marketplace</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">RFQ workflows, AI-powered vessel-cargo matching, and deal rooms for efficient chartering</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Fleet Analytics</h4>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Comprehensive fleet composition, capacity distribution, and builder/owner statistics</p>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">
                    <strong>Perfect for:</strong> Freight forwarders, shippers, traders, insurers, port operators, and commodity desks
                  </p>
                  <Button 
                    className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 shadow-lg"
                    onClick={login}
                    data-testid="cta-maritime-pulse"
                  >
                    Explore Maritime Pulse
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-20 md:py-32 border-t bg-[hsl(var(--secondary))]" id="pricing-section">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              Choose the plan that fits your needs. All plans include 14-day free trial.
            </p>
          </div>

          {/* Product Pricing Tabs */}
          <Tabs defaultValue="maritime" className="w-full max-w-6xl mx-auto mb-8">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
              <TabsTrigger value="maritime">
                <Ship className="h-4 w-4 mr-2" />
                Maritime Pulse
              </TabsTrigger>
              <TabsTrigger value="commerce">
                <TrendingUp className="h-4 w-4 mr-2" />
                Commerce Index
              </TabsTrigger>
            </TabsList>

            {/* Maritime Pulse Pricing */}
            <TabsContent value="maritime">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Starter Tier */}
                <Card className={`relative ${isCurrentPlan('starter') ? 'border-[hsl(var(--success))] border-2' : ''}`} data-testid="pricing-starter">
                  {isCurrentPlan('starter') && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[hsl(var(--success))] text-white px-4 py-1">CURRENT PLAN</Badge>
                    </div>
                  )}
                  <CardHeader className="pb-8">
                    <div className="mb-2">
                      <Badge className="bg-[hsl(var(--info))] text-white">STARTER</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">$99</span>
                      <span className="text-[hsl(var(--muted-foreground))]">/month</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Perfect for small teams</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">100 API calls/day</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Trade Analytics: Morning Reports</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Insurance: Quote Calculator</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Marketplace: 3 RFQs/month</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">14-day free trial</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={login}
                    >
                      {user ? 'Upgrade to Starter' : 'Start Free Trial'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Professional Tier */}
                <Card className={`relative shadow-lg ${isCurrentPlan('professional') ? 'border-[hsl(var(--success))] border-2' : 'border-[hsl(var(--accent))]'}`} data-testid="pricing-professional">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    {isCurrentPlan('professional') ? (
                      <Badge className="bg-[hsl(var(--success))] text-white px-4 py-1">CURRENT PLAN</Badge>
                    ) : (
                      <Badge className="bg-[hsl(var(--accent))] text-white px-4 py-1">MOST POPULAR</Badge>
                    )}
                  </div>
                  <CardHeader className="pb-8 pt-8">
                    <div className="mb-2">
                      <Badge className="bg-[hsl(var(--accent))] text-white">PROFESSIONAL</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">$499</span>
                      <span className="text-[hsl(var(--muted-foreground))]">/month</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">For growing operations</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">1,000 API calls/day</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Full Trade Analytics</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Full Insurance (10 policies/month)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Marketplace: 25 RFQs/month</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Email support</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={login}
                    >
                      {user ? 'Upgrade to Professional' : 'Start Free Trial'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Enterprise Tier */}
                <Card className={`relative ${isCurrentPlan('enterprise') ? 'border-[hsl(var(--success))] border-2' : ''}`} data-testid="pricing-enterprise">
                  {isCurrentPlan('enterprise') && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[hsl(var(--success))] text-white px-4 py-1">CURRENT PLAN</Badge>
                    </div>
                  )}
                  <CardHeader className="pb-8">
                    <div className="mb-2">
                      <Badge className="bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] text-white">ENTERPRISE</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">$1,999</span>
                      <span className="text-[hsl(var(--muted-foreground))]">/month</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">For large organizations</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">10,000+ API calls/day</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">All features unlimited</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Dedicated account manager</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Custom integrations</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Priority support</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={login}
                    >
                      {user ? 'Upgrade to Enterprise' : 'Contact Sales'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Commerce Index Pricing */}
            <TabsContent value="commerce">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Starter Tier - CI */}
                <Card className="relative" data-testid="pricing-commerce-starter">
                  <CardHeader className="pb-8">
                    <div className="mb-2">
                      <Badge className="bg-[hsl(var(--info))] text-white">STARTER</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">$79</span>
                      <span className="text-[hsl(var(--muted-foreground))]">/month</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">For early-stage brands</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">CI Score (24h delayed)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">3 category dashboards</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Top 10 breakout SKUs</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Weekly trend reports</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">14-day free trial</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={login}
                    >
                      Start Free Trial
                    </Button>
                  </CardContent>
                </Card>

                {/* Professional Tier - CI */}
                <Card className="relative shadow-lg border-[hsl(var(--accent))]" data-testid="pricing-commerce-pro">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[hsl(var(--accent))] text-white px-4 py-1">MOST POPULAR</Badge>
                  </div>
                  <CardHeader className="pb-8 pt-8">
                    <div className="mb-2">
                      <Badge className="bg-[hsl(var(--accent))] text-white">PROFESSIONAL</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">$299</span>
                      <span className="text-[hsl(var(--muted-foreground))]">/month</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">For scaling brands</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Real-time CI Score</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">All category dashboards</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Unlimited breakout SKUs</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">7/30-day forecasts</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">CSV exports & API access</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={login}
                    >
                      Start Free Trial
                    </Button>
                  </CardContent>
                </Card>

                {/* Enterprise Tier - CI */}
                <Card className="relative" data-testid="pricing-commerce-enterprise">
                  <CardHeader className="pb-8">
                    <div className="mb-2">
                      <Badge className="bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] text-white">ENTERPRISE</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">$1,499</span>
                      <span className="text-[hsl(var(--muted-foreground))]">/month</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">For large organizations</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Real-time CI + all features</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Custom data integrations</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Multi-seat SSO</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Dedicated account manager</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-[hsl(var(--success))] flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Priority support & SLAs</span>
                      </div>
                    </div>
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => window.location.href = 'mailto:sales@sagisu.com?subject=Enterprise%20Inquiry'}
                    >
                      Contact Sales
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dual CTA Section */}
      <div className="py-20 md:py-32 border-t">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Commerce Index CTA */}
            <div className="text-center p-8 rounded-2xl border-2 border-[hsl(var(--primary))] bg-gradient-to-br from-[hsl(var(--primary))]/5 to-transparent">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-3" style={{ fontFamily: 'var(--font-display)' }}>
                Spot Trends Before They Peak
              </h3>
              <p className="text-[hsl(var(--muted-foreground))] mb-6 max-w-sm mx-auto">
                Join DTC brands and ecommerce operators using Commerce Index to identify breakout products and market momentum in real-time.
              </p>
              <Button
                size="lg"
                onClick={login}
                data-testid="footer-cta-commerce"
                className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 h-12 px-8 shadow-lg"
              >
                Start with Commerce Index
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Maritime Pulse CTA */}
            <div className="text-center p-8 rounded-2xl border-2 border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--accent))]/5 to-transparent">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mx-auto mb-4">
                <Ship className="h-8 w-8 text-[hsl(var(--accent))]" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-3" style={{ fontFamily: 'var(--font-display)' }}>
                Transform Your Maritime Operations
              </h3>
              <p className="text-[hsl(var(--muted-foreground))] mb-6 max-w-sm mx-auto">
                Join freight forwarders and traders using Maritime Pulse to track vessels, optimize routes, and manage freight insurance in one platform.
              </p>
              <Button
                size="lg"
                onClick={login}
                data-testid="footer-cta-maritime"
                className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 h-12 px-8 shadow-lg"
              >
                Start with Maritime Pulse
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}